import React, { useEffect, useRef } from "react";
import { TableProps } from "../../../interfaces/table";
import useModals from "../../../hooks/useModals";
import DeleteModal from "../modals/DeleteModal";
import { usePaymentStore } from "../../../stores/payment";
import Refund from "../../payment/Refund";
import { showToastError } from "../../../services/toast";
import { downloadSlip } from "../../../api/slips";
import PaymentDifference from "../../payment/PaymentDifference";
import { useUtilsStore } from "../../../stores/components/utils";
import {
  Badge,
  DesktopTable,
  DownloadIcon,
  EyeIcon,
  IconButton,
  LinkedRowSummary,
  MicroLabel,
  MobileCardFooter,
  MobileCardList,
  MobileField,
  MobileFieldGrid,
  RefundIcon,
  TableEmptyState,
  TableFrame,
  Td,
  Th,
  TrashIcon,
  cardClasses,
  emptyStateIcon,
  getNumericAccessors,
  renderCellValue,
  rowClasses,
} from "./tableUi";

const TablePayment = ({ columns, data }: TableProps) => {
  const {
    tabPayment,
    setIdPayment,
    setViewOrUpdate,
    openPaiementDifference,
    setOpenPaiementDifference,
  } = usePaymentStore();
  const { searchTerm } = useUtilsStore();

  // Ajout de la ref pour le conteneur du tableau
  const tableContainerRef = useRef<HTMLDivElement>(null);
  // Modals
  const { modals, openModals, openModal, closeModal } = useModals();

  // Fonction pour gérer le clic sur une ligne
  const handleRowClick = (
    e: React.MouseEvent<HTMLTableRowElement | HTMLDivElement>,
    id: number,
  ) => {
    e.stopPropagation();
    e.preventDefault();
    setIdPayment(id);
    setViewOrUpdate("update");
  };

  const handleDownloadSlip = async (id: number) => {
    downloadSlip(id)
      .then((response: any) => {
        const url = window.URL.createObjectURL(
          new Blob([response.data], { type: "application/pdf" }),
        );
        const link = document.createElement("a");
        link.href = url;
        link.setAttribute("download", `bordereau-${id}.pdf`);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      })
      .catch(() => {
        showToastError("Erreur lors du téléchargement du fichier");
      });
  };

  const handleViewSlip = (id: number) => {
    downloadSlip(id)
      .then((response) => {
        const url = window.URL.createObjectURL(
          new Blob([response.data], { type: "application/pdf" }),
        );

        // Ouvrir le PDF dans une nouvelle fenêtre
        window.open(url, "_blank");
      })
      .catch(() => {
        showToastError("Erreur lors du téléchargement du fichier");
      });
  };

  useEffect(() => {
    if (searchTerm !== "" && tableContainerRef.current) {
      tableContainerRef.current.scrollTop = 0;
    }
  }, [searchTerm]);

  const numericAccessors = getNumericAccessors(columns, data);
  const [primaryColumn, ...secondaryColumns] = columns;

  // Le clic sur une cellule d'écart ouvre la modale dédiée
  const handleCellClick = (
    e: React.MouseEvent<HTMLTableCellElement | HTMLDivElement>,
    id: number,
  ) => {
    if (tabPayment === "PaymentDifferences") {
      e.stopPropagation();
      setOpenPaiementDifference(!openPaiementDifference);
      setIdPayment(id);
    }
  };

  const renderCell = (row: Record<string, any>, accessor: string) => {
    if (accessor === "idSlip") {
      return row.idSlip === null ? (
        <Badge tone="slate">Non pointé</Badge>
      ) : (
        <Badge tone="emerald">Pointé</Badge>
      );
    }
    return renderCellValue(row[accessor]);
  };

  const canDelete = (row: Record<string, any>) =>
    (tabPayment !== "Bordereaux" && row.idSlip === null) ||
    tabPayment === "PaymentDifferences";

  // Actions d'une ligne, partagées entre la table et les cartes
  const renderRowActions = (row: Record<string, any>) => (
    <div className="flex items-center gap-1.5">
      {/* Visualisation et téléchargement des bordereaux */}
      {tabPayment === "Bordereaux" ? (
        <>
          <IconButton
            label="Visualiser"
            tone="cyan"
            onClick={(e) => {
              e.stopPropagation();
              handleViewSlip(row.id);
            }}
          >
            <EyeIcon />
          </IconButton>
          <IconButton
            label="Télécharger"
            tone="blue"
            onClick={(e) => {
              e.stopPropagation();
              handleDownloadSlip(row.id);
            }}
          >
            <DownloadIcon />
          </IconButton>
        </>
      ) : null}

      {/* Bouton remboursement pour les encaissements */}
      {tabPayment === "Encaissements" ? (
        <IconButton
          label="Remboursement"
          tone="emerald"
          onClick={(e) => {
            e.stopPropagation();
            openModal(
              "remboursementModal",
              <Refund
                closeModal={() => closeModal("remboursementModal")}
                id={row.id}
                amountTTC={row.amount}
                createOrEdit={"create"}
              />,
            );
          }}
        >
          <RefundIcon />
        </IconButton>
      ) : null}

      {/* Bouton de suppression */}
      {canDelete(row) ? (
        <IconButton
          label="Supprimer"
          tone="danger"
          onClick={(e) => {
            e.stopPropagation();
            openModal(
              "deleteModal",
              <DeleteModal
                closeModal={() => closeModal("deleteModal")}
                id={row.id}
              />,
            );
          }}
        >
          <TrashIcon />
        </IconButton>
      ) : null}
    </div>
  );

  const openRefundEdit = (
    e: React.MouseEvent<HTMLElement>,
    row: Record<string, any>,
    reimboursement: any,
  ) => {
    e.stopPropagation();
    openModal(
      "remboursementModal",
      <Refund
        closeModal={() => closeModal("remboursementModal")}
        id={row.id}
        reimboursementId={reimboursement.id}
        amountTTC={row.amount}
        createOrEdit={"edit"}
      />,
    );
  };

  const renderReimbursementDelete = (reimboursement: any) => (
    <IconButton
      label="Supprimer le remboursement"
      tone="danger"
      onClick={(e) => {
        e.stopPropagation();
        openModal(
          "deleteModal",
          <DeleteModal
            closeModal={() => closeModal("deleteModal")}
            reimboursementId={reimboursement.id}
          />,
        );
      }}
    >
      <TrashIcon />
    </IconButton>
  );

  const renderReimbursementSummary = (reimboursement: any) => (
    <LinkedRowSummary
      label="Remboursement"
      reference={reimboursement.date}
      amountLabel="Montant TTC"
      amount={`${reimboursement.amount}€`}
    />
  );

  const emptyTitle =
    tabPayment === "Encaissements"
      ? "Aucun encaissement trouvé"
      : tabPayment === "PaymentDifferences"
        ? "Aucun écart trouvé"
        : tabPayment === "Bordereaux"
          ? "Aucun bordereau trouvé"
          : "Aucune donnée disponible";

  const emptyDescription =
    tabPayment === "Encaissements"
      ? "Les encaissements apparaîtront ici une fois créés."
      : tabPayment === "PaymentDifferences"
        ? "Les écarts de paiement apparaîtront ici."
        : tabPayment === "Bordereaux"
          ? "Les bordereaux apparaîtront ici une fois créés."
          : "Les données apparaîtront ici.";

  return (
    <div className="flex min-h-0 flex-1 flex-col">
      {/* Affichage des modales en fonction de openModals */}
      {Object.keys(modals).map((modalId) =>
        openModals[modalId] ? <div key={modalId}>{modals[modalId]}</div> : null,
      )}

      {openPaiementDifference && (
        <PaymentDifference
          closeModal={() => setOpenPaiementDifference(!openPaiementDifference)}
        />
      )}

      <TableFrame scrollRef={tableContainerRef}>
        {data.length === 0 ? (
          <TableEmptyState
            title={emptyTitle}
            description={emptyDescription}
            icon={emptyStateIcon(
              tabPayment === "Bordereaux"
                ? "M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                : "M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z",
            )}
          />
        ) : (
          <>
            {/* Vue tableau — écrans larges */}
            <DesktopTable>
              <thead className="sticky top-0 z-10">
                <tr>
                  {columns.map((col, index) => (
                    <Th
                      key={index}
                      align={numericAccessors.has(col.accessor) ? "right" : "left"}
                      width={col.header === "Dossier" ? "28%" : undefined}
                    >
                      {col.header}
                    </Th>
                  ))}
                  <Th align="right" className="w-px">
                    Actions
                  </Th>
                </tr>
              </thead>
              <tbody className="font-Nunito">
                {data.map((row, rowIndex) => {
                  const hasReimboursement =
                    row?.externalReimbursements?.length > 0;

                  return (
                    <React.Fragment key={rowIndex}>
                      {/* Ligne principale */}
                      <tr
                        onClick={(e) => handleRowClick(e, row.id)}
                        className={rowClasses({
                          highlighted: hasReimboursement,
                        })}
                      >
                        {/* Affiche les données de chaque colonne */}
                        {columns.map((col, colIndex) => (
                          <Td
                            key={colIndex}
                            align={
                              numericAccessors.has(col.accessor)
                                ? "right"
                                : "left"
                            }
                            className={
                              col.header === "Dossier"
                                ? "font-semibold text-slate-900"
                                : "text-slate-600"
                            }
                            onClick={(e) => handleCellClick(e, row.id)}
                          >
                            {renderCell(row, col.accessor)}
                          </Td>
                        ))}

                        <Td>
                          <div className="flex justify-end">
                            {renderRowActions(row)}
                          </div>
                        </Td>
                      </tr>

                      {/* Lignes des remboursements liés */}
                      {row?.externalReimbursements?.map(
                        (reimboursement: any, reimboursementIndex: any) => (
                          <tr
                            key={`${rowIndex}-credit-${reimboursementIndex}`}
                            className="cursor-pointer bg-amber-50/40 transition-colors hover:bg-amber-100/50"
                            onClick={(e) =>
                              openRefundEdit(e, row, reimboursement)
                            }
                          >
                            <Td colSpan={columns.length} dense indent>
                              {renderReimbursementSummary(reimboursement)}
                            </Td>
                            <Td dense>
                              <div className="flex justify-end">
                                {renderReimbursementDelete(reimboursement)}
                              </div>
                            </Td>
                          </tr>
                        ),
                      )}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </DesktopTable>

            {/* Vue cartes — mobile et tablette */}
            <MobileCardList>
              {data.map((row, rowIndex) => {
                const hasReimboursement =
                  row?.externalReimbursements?.length > 0;

                return (
                  <li key={rowIndex}>
                    <div
                      onClick={(e) => handleRowClick(e, row.id)}
                      className={cardClasses({
                        highlighted: hasReimboursement,
                      })}
                    >
                      <div
                        className="min-w-0"
                        onClick={(e) => handleCellClick(e, row.id)}
                      >
                        <MicroLabel>{primaryColumn?.header}</MicroLabel>
                        <p className="mt-0.5 text-[15px] leading-snug font-bold wrap-break-word text-slate-900">
                          {primaryColumn
                            ? renderCell(row, primaryColumn.accessor)
                            : null}
                        </p>
                      </div>

                      {secondaryColumns.length > 0 && (
                        <MobileFieldGrid>
                          {secondaryColumns.map((col) => (
                            <MobileField
                              key={col.accessor}
                              label={col.header}
                              value={renderCell(row, col.accessor)}
                              numeric={numericAccessors.has(col.accessor)}
                            />
                          ))}
                        </MobileFieldGrid>
                      )}

                      <MobileCardFooter>
                        {renderRowActions(row)}
                      </MobileCardFooter>
                    </div>

                    {/* Remboursements liés */}
                    {row?.externalReimbursements?.map(
                      (reimboursement: any, reimboursementIndex: any) => (
                        <div
                          key={`${rowIndex}-credit-${reimboursementIndex}`}
                          onClick={(e) => openRefundEdit(e, row, reimboursement)}
                          className="mt-1.5 ml-4 flex cursor-pointer flex-wrap items-center justify-between gap-2 rounded-xl border border-amber-200 bg-amber-50/60 p-3"
                        >
                          {renderReimbursementSummary(reimboursement)}
                          {renderReimbursementDelete(reimboursement)}
                        </div>
                      ),
                    )}
                  </li>
                );
              })}
            </MobileCardList>
          </>
        )}
      </TableFrame>
    </div>
  );
};

export default TablePayment;
